import { Link } from "react-router-dom";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect } from "react";

import settingsLogo from "../assets/settings-logo.png";
import thermoimagingLogo from "../assets/thermoimaging-logo.png";
import thicknessPlotterLogo from "../assets/thickness-plotter-logo.png";
import { trackAnalyticsEvent } from "../analytics";
import { isShowMode } from "../showMode";
import {
  onShowTourRequest,
  scheduleShowTourStart,
  shouldAutoStartShowTour,
  startShowTour,
  tourElement
} from "../tours/showTour";

type HomeTile = {
  to: string;
  title: string;
  description: string;
  style: CSSProperties;
  overlay: string;
  imageSrc?: string;
  showInPublic?: boolean;
  customVisual?: ReactNode;
};

export default function HomePage() {
  const tiles: HomeTile[] = [
    {
      to: "/thermoimaging",
      title: "Thermoimaging",
      description: "Filter, visualize, and analyze thermal profiles.",
      style: {},
      overlay: "linear-gradient(180deg, rgba(10,18,30,0.2), rgba(10,18,30,0.75))",
      imageSrc: thermoimagingLogo,
      showInPublic: true
    },
    {
      to: "/thickness",
      title: "Thickness Plotter",
      description: "Bottle thickness visualization module.",
      style: {},
      overlay: "linear-gradient(180deg, rgba(10,18,30,0.2), rgba(10,18,30,0.75))",
      imageSrc: thicknessPlotterLogo,
      showInPublic: true
    },
    {
      to: "/color",
      title: "Color",
      description: "Sync, compare, and report UV-Vis color measurements.",
      style: {
        background:
          "radial-gradient(circle at 22% 18%, rgba(255, 233, 162, 0.65), transparent 30%), radial-gradient(circle at 78% 24%, rgba(95, 184, 255, 0.35), transparent 32%), linear-gradient(135deg, rgba(34,81,69,0.95), rgba(204,102,51,0.88))"
      },
      overlay: "linear-gradient(180deg, rgba(10,18,30,0.16), rgba(10,18,30,0.72))",
      customVisual: (
        <div className="home-tile-visual">
          {["#f8d66d", "#c7f0ff", "#cc6633", "#225145"].map((color, index) => (
            <div
              key={color}
              style={{
                width: index % 2 === 0 ? "min(56px, 14vw)" : "min(40px, 10vw)",
                height: index % 2 === 0 ? "min(56px, 14vw)" : "min(40px, 10vw)",
                borderRadius: "999px",
                background: color,
                boxShadow: "0 10px 22px rgba(0,0,0,0.22)",
                border: "2px solid rgba(255,255,255,0.4)"
              }}
            />
          ))}
        </div>
      )
    },
    {
      to: "/settings",
      title: "Settings",
      description: "NAS mount config and app preferences.",
      style: {},
      overlay: "linear-gradient(180deg, rgba(10,18,30,0.18), rgba(10,18,30,0.72))",
      imageSrc: settingsLogo
    }
  ];

  const visibleTiles = isShowMode ? tiles.filter((tile) => tile.showInPublic) : tiles;
  const startHomeTour = useCallback(() => {
    startShowTour("home", [
      {
        element: tourElement("home-hero"),
        popover: {
          title: "Welcome",
          description:
            "This public interface contains curated thermoimaging and thickness analyses.",
          side: "bottom",
          align: "start"
        }
      },
      {
        element: tourElement("home-thermo-tile"),
        popover: {
          title: "Thermoimaging",
          description:
            "Open this module to choose a curated thermo set and inspect thermal profiles.",
          side: "top",
          align: "center"
        }
      },
      {
        element: tourElement("home-thickness-tile"),
        popover: {
          title: "Thickness Plotter",
          description:
            "Open this module to choose a curated thickness set and inspect bottle plots.",
          side: "top",
          align: "center"
        }
      },
      {
        element: tourElement("app-tour-toggle"),
        popover: {
          title: "Tutorial control",
          description:
            "Use this switch to turn automatic tutorials on or off. Turning it on starts the current page tutorial.",
          side: "bottom",
          align: "end"
        }
      }
    ]);
  }, []);

  useEffect(() => onShowTourRequest("home", startHomeTour), [startHomeTour]);

  useEffect(() => {
    if (!shouldAutoStartShowTour("home")) return;
    return scheduleShowTourStart(startHomeTour);
  }, [startHomeTour]);

  return (
    <div className="hero" data-tour-id="home-hero">
      <h1>Process Intelligence</h1>
      <p>Choose a module to get started.</p>
      <div className="home-module-grid">
        {visibleTiles.map((tile) => (
          <Link
            key={tile.to}
            to={tile.to}
            className="button-tile home-module-tile"
            data-tour-id={
              tile.to === "/thermoimaging"
                ? "home-thermo-tile"
                : tile.to === "/thickness"
                ? "home-thickness-tile"
                : undefined
            }
            style={tile.style}
            onClick={() => {
              if (tile.to === "/thermoimaging") trackAnalyticsEvent("nav_thermoimaging_opened");
              if (tile.to === "/thickness") trackAnalyticsEvent("nav_thickness_opened");
            }}
          >
            {tile.imageSrc ? (
              <img className="home-tile-logo" src={tile.imageSrc} alt="" aria-hidden="true" />
            ) : (
              tile.customVisual || null
            )}
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: tile.overlay
              }}
            />
            <div className="home-tile-content">
              <div className="home-tile-title">{tile.title}</div>
              <div className="home-tile-copy">{tile.description}</div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
